'use client';
import { useEffect, useState } from 'react';
import { Clinic, Professional, ProfessionalType } from '@interface/clinic';
import { Product } from '@interface/product';
import { fetchClinics } from '@utils/fetch';
import { useIsMobileDevice } from 'app/hooks/useIsMobileDevice';
import Carousel from 'designSystem/Carousel/Carousel';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { Text, Title } from 'designSystem/Texts/Texts';
import Image from 'next/image';

import ProductSelectorButton from './ProductSelectorButton';
import VithasProfessionals from './VithasProfessionals';

async function getClinics(): Promise<Clinic[]> {
  const clinics = await fetchClinics();
  const professionalNames = new Set<string>();

  let clinicsWithDistinctProfessionals = clinics;

  clinicsWithDistinctProfessionals = clinicsWithDistinctProfessionals.map(
    clinic => {
      const distinctProfessionals: Professional[] = clinic.professionals.reduce(
        (acc, professional) => {
          if (
            !acc.find(p => p.name === professional.name) &&
            !professionalNames.has(professional.name)
          ) {
            acc.push(professional);
            professionalNames.add(professional.name);
          }
          return acc;
        },
        [] as Professional[]
      );

      return {
        ...clinic,
        professionals: distinctProfessionals,
      };
    }
  );

  return clinicsWithDistinctProfessionals;
}

export default function VithasGroup({
  product = null,
  showButton = true,
  isMobile,
}: {
  product?: Product | null;
  showButton?: boolean;
  /** Lo calcula el Server Component padre con `isMobileSSR()`. */
  isMobile?: boolean;
}) {
  const detectedIsMobile = useIsMobileDevice();
  const isMobileDevice = isMobile ?? detectedIsMobile;

  const [professionals, setProfessionals] = useState<Professional[]>([]);

  useEffect(() => {
    getClinics().then(clinics => {
      const processedProfessionals = clinics
        .flatMap(clinic =>
          clinic.professionals.map(professional => {
            if (
              professional.professionalType === ProfessionalType.Admin &&
              professional.active
            ) {
              return {
                ...professional,
                city: clinic.city,
              };
            }
            return null;
          })
        )
        .filter(
          professional =>
            professional !== null &&
            professional.urlPhoto !== '' &&
            professional.professionalType === ProfessionalType.Admin &&
            professional.flowwwId == '0'
        ) as Professional[];
      const sortedProfessionals = processedProfessionals.sort(
        (x, y) => x.order - y.order
      );

      setProfessionals(sortedProfessionals);
    });
  }, []);
  const vithasImages = [
    '/images/surgery/vithas1.jpg',
    '/images/surgery/vithas2.jpg',
    '/images/surgery/vithas3.jpg',
    '/images/surgery/vithas4.jpg',
  ];

  if (!isMobileDevice) {
    return (
      <>
        <div className="w-full bg-hg-cream300">
          <Container className="gap-16 justify-between py-8 px-0 md:px-4 md:flex md:flex-row md:items-start md:pb-4 pdpExplanationTitle">
            <Container className="md:px-0 md:flex md:flex-col md:justify-start md:items-start w-full">
              <Title isAnimated size="2xl" className="font-bold mt-4">
                Cirugía Estética
              </Title>
              <Title isAnimated size="xl" className="font-bold">
                con Profesionales de Primer Nivel
              </Title>
              <p className="text-hg-black500 mb-4 last:mb-8">
                Nuestros tratamientos de cirugía estética se realizan en
                colaboración con Vithas, uno de los grupos hospitalarios más
                prestigiosos de España, con más de 20.000 intervenciones de
                cirugía estética realizadas y reconocido por su excelencia
                médica y tecnología de vanguardia.
              </p>
              <Flex
                layout="row-center"
                className="gap-2 items-center justify-center w-full mb-5 mt-5"
              >
                <Image
                  src="/images/holaglowLogo.png"
                  height={60}
                  width={120}
                  alt="Holaglow"
                  className="h-12 w-auto"
                />
                <span className="text-xl font-bold text-hg-black500 mx-2">
                  +
                </span>
                <Image
                  src="/images/vithas.png"
                  height={60}
                  width={120}
                  alt="Vithas"
                  className="h-12 w-auto"
                />
              </Flex>
            </Container>
            <Container className="md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
              <div className="relative w-full">
                <Carousel
                  controlstyles="px-4"
                  hasControls
                  hasCounter={isMobileDevice}
                  dragEnabled
                  touchEnabled
                  isFullWidth={true}
                  visibleSlides={1}
                >
                  {vithasImages.map((item, i) => (
                    <Image
                      src={item}
                      width={1000}
                      height={1000}
                      key={i}
                      priority
                      alt="Vithas profesionales"
                      className="w-full h-auto rounded-3xl"
                    />
                  ))}
                </Carousel>
              </div>
            </Container>
          </Container>
          <Container className="gap-16 justify-between py-8 px-0 md:px-4 md:flex md:flex-row md:items-start md:pb-4">
            <Container className="md:w-1/3 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
              <Text
                isAnimated
                className="mb-4 text-hg-secondary font-gtUltra text-xldr"
              >
                Cirujanos Especializados
              </Text>
              <ul className="flex flex-col gap-3">
                Cirujanos plásticos altamente especializados, con amplia
                experiencia en procedimientos estéticos y reconstructivos.
              </ul>
            </Container>
            <Container className="md:w-1/3 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
              <Text
                isAnimated
                className="mb-4 text-hg-secondary font-gtUltra text-xldr"
              >
                Entornos Seguros
              </Text>
              <ul className="flex flex-col gap-3">
                Intervenciones realizadas en entornos hospitalarios seguros, con
                los más altos estándares de calidad clínica.
              </ul>
            </Container>
            <Container className="md:w-1/3 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
              <Text
                isAnimated
                className="mb-4 text-hg-secondary font-gtUltra text-xldr"
              >
                Planes Personalizados
              </Text>
              <ul className="flex flex-col gap-3">
                Planes quirúrgicos 100% personalizados, diseñados según tus
                necesidades, tu anatomía y tus objetivos estéticos.
              </ul>
            </Container>
          </Container>
          {product && showButton && (
            <Flex
              layout="col-center"
              className="w-full px-4 py-8 md:px-0 md:py-12 flex justify-center items-center"
            >
              <ProductSelectorButton product={product} />
            </Flex>
          )}
        </div>
        {professionals.length > 0 && (
          <VithasProfessionals professionals={professionals} />
        )}
      </>
    );
  }
  return (
    <div className="w-full bg-hg-cream300">
      <div className="relative w-full">
        <Image
          src="/images/surgery/vithas-group-header.png"
          width={1000}
          height={1000}
          priority
          alt="Vithas profesionales"
          className="w-full h-auto rounded-b-3xl"
        />
      </div>
      <Container className="gap-16 justify-between py-8 px-0 md:px-4 md:flex md:flex-row md:items-start md:pb-4 pdpExplanationTitle">
        <Container className="md:px-0 md:flex md:flex-col md:justify-start md:items-start w-full">
          <Title isAnimated size="2xl" className="font-bold mt-4">
            Cirugía Estética
          </Title>
          <Title isAnimated size="xl" className="font-bold">
            con Profesionales de Primer Nivel
          </Title>
          <p className="text-hg-black500 mb-4 last:mb-8">
            Nuestros tratamientos de cirugía estética se realizan en
            colaboración con Vithas, uno de los grupos hospitalarios más
            prestigiosos de España, con más de 20.000 intervenciones de cirugía
            estética realizadas y reconocido por su excelencia médica y
            tecnología de vanguardia.
          </p>
        </Container>
        <Flex
          layout="row-center"
          className="gap-2 items-center justify-center w-full mb-5"
        >
          <Image
            src="/images/holaglowLogo.png"
            height={60}
            width={120}
            alt="Holaglow"
            className="h-12 w-auto"
          />
          <span className="text-xl font-bold text-hg-black500 mx-2">+</span>
          <Image
            src="/images/vithas.png"
            height={60}
            width={120}
            alt="Vithas"
            className="h-12 w-auto"
          />
        </Flex>
        <Container className="md:w-1/2 md:px-0 md:flex md:flex-col md:justify-start md:items-start">
          <Text
            isAnimated
            className="mb-4 text-hg-secondary font-gtUltra text-xldr"
          >
            Cirujanos Especializados
          </Text>
          <ul className="flex flex-col gap-3">
            Cirujanos plásticos altamente especializados, con amplia experiencia
            en procedimientos estéticos y reconstructivos.
          </ul>
          <Text
            isAnimated
            className="mb-4 text-hg-secondary font-gtUltra text-xldr mt-4"
          >
            Entornos Seguros
          </Text>
          <ul className="flex flex-col gap-3">
            Intervenciones realizadas en entornos hospitalarios seguros, con los
            más altos estándares de calidad clínica.
          </ul>
          <Text
            isAnimated
            className="mb-4 text-hg-secondary font-gtUltra text-xldr mt-4"
          >
            Planes Personalizados
          </Text>
          <ul className="flex flex-col gap-3">
            Planes quirúrgicos 100% personalizados, diseñados según tus
            necesidades, tu anatomía y tus objetivos estéticos.
          </ul>
        </Container>
      </Container>

      {product && showButton && (
        <Flex
          layout="col-center"
          className="w-full px-4 py-8 md:px-0 md:py-12 flex justify-center items-center"
        >
          <ProductSelectorButton product={product} />
        </Flex>
      )}
      {professionals.length > 0 && (
        <VithasProfessionals professionals={professionals} />
      )}
    </div>
  );
}
